/**
 * EmptySlotCell Component
 * 
 * Task 4.4: Create EmptySlotCell component for empty slots
 * Requirements: 8.9, 8.12
 * 
 * Displays empty period slots with dashed border, placeholder text,
 * and hover effects in Edit Mode.
 */

import { Box, Typography, styled } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';

// ============================================================================
// Styled Components
// ============================================================================

const EmptyCell = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'editMode',
})<{ editMode?: boolean }>(({ theme, editMode }) => ({
  width: '100%',
  height: '100%',
  minHeight: '52px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(0.5),
  borderRadius: theme.shape.borderRadius,
  // View mode: invisible/minimal — no border, no background
  border: editMode ? `2px dashed ${theme.palette.divider}` : 'none',
  backgroundColor: editMode ? theme.palette.background.paper : 'transparent',
  cursor: editMode ? 'pointer' : 'default',
  transition: 'all 0.2s',
  color: theme.palette.text.disabled,
  
  ...(editMode && {
    '&:hover': {
      borderColor: theme.palette.primary.main,
      borderStyle: 'solid',
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.primary.main,
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[2],
    },
  }),
}));

// ============================================================================
// Component Props
// ============================================================================

export interface EmptySlotCellProps {
  editMode: boolean;
  onClick?: () => void;
}

// ============================================================================
// Main Component
// ============================================================================

export function EmptySlotCell({ editMode, onClick }: EmptySlotCellProps) {
  return (
    <EmptyCell
      editMode={editMode}
      onClick={onClick}
      role="button"
      aria-label={editMode ? 'Add period - click to assign teacher and subject' : 'Empty slot'}
      tabIndex={editMode ? 0 : -1}
      onKeyDown={(e) => {
        if (editMode && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      {editMode && (
        <>
          <AddIcon fontSize="small" />
          <Typography variant="caption" fontWeight={500}>Add Period</Typography>
        </>
      )}
    </EmptyCell>
  );
}
